import React, { useState, useEffect } from 'react';
import { dataAPI } from '../services/api';
import './Calendar.css';

const PLATFORMS = {
  instagram: { label: 'Instagram', color: '#C13584', bg: '#FCE8F3' },
  tiktok: { label: 'TikTok', color: '#222222', bg: '#EBEBEB' },
  youtube: { label: 'YouTube', color: '#C4302B', bg: '#FCEAEA' },
  facebook: { label: 'Facebook', color: '#1877F2', bg: '#E8F0FE' },
  linkedin: { label: 'LinkedIn', color: '#0A66C2', bg: '#E8F3FB' }
};

const OWNERS = {
  coach: { label: 'Coach', color: '#1A6BB5', bg: '#D6E8F7' },
  assistante: { label: 'Assistante', color: '#4A44AA', bg: '#EDE8FA' },
  both: { label: 'Les deux', color: '#0A9962', bg: '#D6F0E5' }
};

const STATUSES = { todo: 'A faire', prog: 'En cours', done: 'Publie' };
const CONTENT_TYPES = { reel: 'Reel/Short', post: 'Post/Carousel', story: 'Story', video: 'Video longue', live: 'Live' };
const MONTHS = ['', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const Calendar = () => {
  const [content, setContent] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [platformFilter, setPlatformFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    heure: '09:00',
    platform: 'instagram',
    content_type: 'reel',
    title: '',
    owner: 'coach',
    note: '',
    status: 'todo',
    recurrence: 'none',
    recurrence_days: 7,
    recurrence_end: ''
  });

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await dataAPI.getContent();
      if (response.data.success) {
        setContent(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load content', error);
    }
  };

  const saveContent = async (updatedContent) => {
    try {
      const response = await dataAPI.saveContent(updatedContent);
      if (!response.data.success) {
        console.error('Save failed:', response.data.message);
      }
    } catch (error) {
      console.error('Failed to save', error);
    }
  };

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const days = [];

    // Empty cells for days before the first day
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, date: null });
    }

    // Days of the month
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({ day: d, date: dateStr });
    }

    return days;
  };

  const getContentForDate = (dateStr) => {
    return content.filter((c) => {
      const matchDate = c.date === dateStr;
      const matchPlatform = platformFilter === 'all' || c.platform === platformFilter;
      const matchOwner = ownerFilter === 'all' || c.owner === ownerFilter;
      return matchDate && matchPlatform && matchOwner;
    });
  };

  const openModal = (dateStr = null) => {
    setEditingItem(null);
    setFormData({
      date: dateStr || new Date().toISOString().slice(0, 10),
      heure: '09:00',
      platform: 'instagram',
      content_type: 'reel',
      title: '',
      owner: 'coach',
      note: '',
      status: 'todo',
      recurrence: 'none',
      recurrence_days: 7,
      recurrence_end: ''
    });
    setShowModal(true);
  };

  const handleSave = () => {
    const newItem = {
      ...formData,
      id: editingItem ? editingItem.id : Date.now(),
      group_id: editingItem ? editingItem.group_id : null
    };

    let updatedContent;
    if (editingItem) {
      updatedContent = content.map((c) => (c.id === editingItem.id ? newItem : c));
    } else {
      updatedContent = [...content, newItem];
      if (formData.recurrence !== 'none') {
        const endDate = formData.recurrence_end ? new Date(formData.recurrence_end) : new Date(formData.date);
        endDate.setMonth(endDate.getMonth() + 3);
        let currentDate = new Date(formData.date);

        while (currentDate <= endDate) {
          if (formData.recurrence === 'daily') {
            currentDate.setDate(currentDate.getDate() + 1);
          } else if (formData.recurrence === 'weekly') {
            currentDate.setDate(currentDate.getDate() + 7);
          } else if (formData.recurrence === 'biweekly') {
            currentDate.setDate(currentDate.getDate() + 14);
          } else if (formData.recurrence === 'monthly') {
            currentDate.setMonth(currentDate.getMonth() + 1);
          } else if (formData.recurrence === 'custom') {
            currentDate.setDate(currentDate.getDate() + formData.recurrence_days);
          }

          if (currentDate <= endDate && currentDate.toISOString().slice(0, 10) !== formData.date) {
            updatedContent.push({
              ...newItem,
              id: Date.now() + Math.random(),
              date: currentDate.toISOString().slice(0, 10),
              group_id: newItem.id
            });
          }
        }
      }
    }

    setContent(updatedContent);
    saveContent(updatedContent);
    setShowModal(false);
  };

  const handleDelete = (id) => {
    const updatedContent = content.filter((c) => c.id !== id);
    setContent(updatedContent);
    saveContent(updatedContent);
  };

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const calendarDays = generateCalendarDays();

  return (
    <div className="calendar-page">
      <div className="section-header">
        <div className="section-dot"></div>
        <h2>Calendrier Contenu</h2>
        <span className="month-label">{MONTHS[currentMonth + 1]} {currentYear}</span>
        <div className="header-actions">
          <button className="cal-nav" onClick={prevMonth}>&#8249;</button>
          <button className="cal-nav" onClick={nextMonth}>&#8250;</button>
          <button className="btn-primary" onClick={() => openModal()}>+ Ajouter</button>
        </div>
      </div>

      <div className="filter-chips">
        <div className={`chip ${platformFilter === 'all' ? 'active' : ''}`} onClick={() => setPlatformFilter('all')}>Toutes</div>
        {Object.entries(PLATFORMS).map(([key, val]) => (
          <div key={key} className={`chip ${platformFilter === key ? 'active' : ''}`} onClick={() => setPlatformFilter(key)}>{val.label}</div>
        ))}
      </div>

      <div className="filter-chips">
        <div className={`chip ${ownerFilter === 'all' ? 'active' : ''}`} onClick={() => setOwnerFilter('all')}>Tous</div>
        {Object.entries(OWNERS).map(([key, val]) => (
          <div key={key} className={`chip owner-chip ${ownerFilter === key ? 'active' : ''}`} 
               style={{ borderColor: val.color, color: val.color }}
               onClick={() => setOwnerFilter(key)}>{val.label}</div>
        ))}
      </div>

      <div className="calendar-grid">
        <div className="calendar-dow">
          {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map((d) => (
            <div key={d} className="dow-cell">{d}</div>
          ))}
        </div>
        <div className="calendar-days">
          {calendarDays.map((item, idx) => {
            const dayContent = item.date ? getContentForDate(item.date) : [];
            return (
              <div key={idx} className={`calendar-cell ${!item.day ? 'empty' : ''} ${item.date === today ? 'today' : ''}`}>
                {item.day && (
                  <>
                    <div className="cell-day">{item.day}</div>
                    <div className="cell-content">
                      {dayContent.map((c) => (
                        <div
                          key={c.id}
                          className="content-pill"
                          style={{ backgroundColor: PLATFORMS[c.platform]?.bg, borderLeftColor: PLATFORMS[c.platform]?.color }}
                          onClick={() => { setEditingItem(c); setFormData(c); setShowModal(true); }}
                        >
                          <div className="pill-header">
                            <span className="pill-platform">{PLATFORMS[c.platform]?.label}</span>
                            <span className="pill-owner" style={{ backgroundColor: OWNERS[c.owner]?.bg, color: OWNERS[c.owner]?.color }}>
                              {OWNERS[c.owner]?.label}
                            </span>
                          </div>
                          <div className="pill-title">{c.title}</div>
                          <div className="pill-status">{STATUSES[c.status]}</div>
                          <button className="pill-delete" onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}>×</button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <h3>{editingItem ? 'Modifier' : 'Nouveau'} contenu</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Date</label>
                <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Heure</label>
                <input type="time" value={formData.heure} onChange={(e) => setFormData({ ...formData, heure: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Plateforme</label>
                <select value={formData.platform} onChange={(e) => setFormData({ ...formData, platform: e.target.value })}>
                  {Object.entries(PLATFORMS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Type</label>
                <select value={formData.content_type} onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}>
                  {Object.entries(CONTENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Titre / Sujet</label>
              <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Ex: Transformation Amine" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Responsable</label>
                <select value={formData.owner} onChange={(e) => setFormData({ ...formData, owner: e.target.value })}>
                  {Object.entries(OWNERS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Statut</label>
                <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                  {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Note</label>
              <textarea value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} rows="2" />
            </div>
            <div className="form-group">
              <label>Recurrence</label>
              <select value={formData.recurrence} onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}>
                <option value="none">Aucune</option>
                <option value="daily">Tous les jours</option>
                <option value="weekly">Chaque semaine</option>
                <option value="biweekly">Toutes les 2 semaines</option>
                <option value="monthly">Chaque mois</option>
                <option value="custom">Personnalise</option>
              </select>
            </div>
            {formData.recurrence === 'custom' && (
              <div className="form-group">
                <label>Repeter tous les X jours</label>
                <input type="number" min="1" value={formData.recurrence_days} onChange={(e) => setFormData({ ...formData, recurrence_days: e.target.value })} />
              </div>
            )}
            {formData.recurrence !== 'none' && (
              <div className="form-group">
                <label>Repeter jusqu'au</label>
                <input type="date" value={formData.recurrence_end} onChange={(e) => setFormData({ ...formData, recurrence_end: e.target.value })} />
              </div>
            )}
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleSave}>Enregistrer</button>
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
